import { z } from "zod";
import { syncColumnsSchema } from "./common.js";

export const gymSchema = syncColumnsSchema.extend({
  name: z.string().min(1).max(80),
  isDefault: z.boolean(),
});

export type Gym = z.infer<typeof gymSchema>;

/**
 * Tipo de equipamiento. Cada fila de `equipment` es una INSTANCIA FISICA
 * concreta: 80 kg en una maquina no son 80 kg en otra (ADR-014).
 */
export const equipmentKindSchema = z.enum([
  "barbell",
  "dumbbell_set",
  "machine_stack",
  "machine_plate_loaded",
  "cable",
  "smith",
  "bodyweight",
  "band",
  "other",
]);

export type EquipmentKind = z.infer<typeof equipmentKindSchema>;

/** Inventario de discos: peso del disco y numero de PARES disponibles. */
export const plateInventoryItemSchema = z.object({
  plateKg: z.number().positive().max(50),
  pairs: z.number().int().min(0).max(20),
});

/**
 * Forma base sin refinamientos. Se exporta para poder inspeccionar los campos
 * (p. ej. la comprobacion de columnas en `columnas.test.ts`).
 */
export const equipmentBaseSchema = syncColumnsSchema.extend({
  gymId: z.uuid(),
  kind: equipmentKindSchema,
  label: z.string().min(1).max(120),
  /** Peso real de la barra o del carro, cuando aplica. */
  barWeightKg: z.number().min(0).max(60).nullable(),
  minLoadKg: z.number().min(0).max(500).nullable(),
  maxLoadKg: z.number().min(0).max(1000).nullable(),
  incrementKg: z.number().positive().max(50).nullable(),
  hasMicroIncrements: z.boolean(),
  microIncrementKg: z.number().positive().max(10).nullable(),
  /** Solo para barras y maquinas de discos. */
  plateInventory: z.array(plateInventoryItemSchema),
  /** Solo para juegos de mancuernas: lista explicita de pesos disponibles. */
  dumbbellsKg: z.array(z.number().positive().max(120)),
  notes: z.string().max(500).nullable(),
});

export const equipmentSchema = equipmentBaseSchema.superRefine((eq, ctx) => {
  if (eq.hasMicroIncrements && eq.microIncrementKg === null) {
    ctx.addIssue({
      code: "custom",
      path: ["microIncrementKg"],
      message: "microIncrementKg es obligatorio si hasMicroIncrements es true",
    });
  }
  if (
    eq.minLoadKg !== null &&
    eq.maxLoadKg !== null &&
    eq.maxLoadKg < eq.minLoadKg
  ) {
    ctx.addIssue({
      code: "custom",
      path: ["maxLoadKg"],
      message: "maxLoadKg no puede ser menor que minLoadKg",
    });
  }
});

export type Equipment = z.infer<typeof equipmentSchema>;
export type PlateInventoryItem = z.infer<typeof plateInventoryItemSchema>;

export const loadSourceSchema = z.enum([
  "stack",
  "dumbbell",
  "computed_plates",
  "bodyweight",
]);

/** Carga REALMENTE seleccionable en una instancia de equipamiento. */
export const availableLoadSchema = syncColumnsSchema.extend({
  equipmentId: z.uuid(),
  loadKg: z.number().min(0).max(1000),
  source: loadSourceSchema,
});

export type AvailableLoad = z.infer<typeof availableLoadSchema>;
export type LoadSource = z.infer<typeof loadSourceSchema>;
